<template>
  <div>
    <div class="auth-page">
      <div class="auth-card">
        <router-link to="/login" class="back-link">
          <i class="bi bi-arrow-left"></i>
          <span>Back to Login</span>
        </router-link>

        <!-- STEP 1: NO TOKEN IN URL -->
        <template v-if="!token">
          <div class="auth-icon error">
            <i class="bi bi-exclamation-triangle"></i>
          </div>
          <h1>Invalid reset link</h1>
          <p class="auth-subtitle">
            This password reset link is missing or malformed. Please request a new one.
          </p>
          <router-link to="/forgot-password" class="btn-primary">
            Request a new link
          </router-link>
        </template>

        <!-- STEP 2: RESET FORM -->
        <template v-else-if="step === 'form'">
          <div class="auth-icon">
            <i class="bi bi-shield-lock"></i>
          </div>
          <h1>Set a new password</h1>
          <p class="auth-subtitle">
            Choose a new password for your account.
          </p>

          <form @submit.prevent="submitNewPassword" class="auth-form">
            <label for="password">New password</label>
            <input
              id="password"
              type="password"
              v-model="password"
              placeholder="At least 8 characters"
              required
              minlength="8"
              :disabled="loading"
              autocomplete="new-password"
            >

            <label for="confirmPassword">Confirm new password</label>
            <input
              id="confirmPassword"
              type="password"
              v-model="confirmPassword"
              placeholder="Re-enter your new password"
              required
              minlength="8"
              :disabled="loading"
              autocomplete="new-password"
            >

            <p v-if="errorMessage" class="form-error">
              <i class="bi bi-exclamation-circle"></i> {{ errorMessage }}
            </p>

            <button type="submit" class="btn-primary" :disabled="loading">
              <span v-if="loading">
                <i class="bi bi-arrow-repeat spin"></i> Updating...
              </span>
              <span v-else>
                Update password
              </span>
            </button>
          </form>
        </template>

        <!-- STEP 3: SUCCESS -->
        <template v-else-if="step === 'success'">
          <div class="auth-icon success">
            <i class="bi bi-check-circle"></i>
          </div>
          <h1>Password updated</h1>
          <p class="auth-subtitle">
            Your password has been changed successfully. You can now log in with your new password.
          </p>
          <router-link to="/login" class="btn-primary">
            Go to Login
          </router-link>
        </template>

        <!-- STEP 4: TOKEN INVALID / EXPIRED (from backend) -->
        <template v-else-if="step === 'invalid'">
          <div class="auth-icon error">
            <i class="bi bi-exclamation-triangle"></i>
          </div>
          <h1>Link expired or already used</h1>
          <p class="auth-subtitle">
            This reset link is no longer valid. Please request a new one.
          </p>
          <router-link to="/forgot-password" class="btn-primary">
            Request a new link
          </router-link>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  name: "ResetPassword",
  data() {
    return {
      token: "",
      step: "form", // 'form' | 'success' | 'invalid'
      password: "",
      confirmPassword: "",
      loading: false,
      errorMessage: "",
    };
  },

  created() {
    this.token = this.$route.query.token || "";
  },

  methods: {
    async submitNewPassword() {
      this.errorMessage = "";

      if (this.password !== this.confirmPassword) {
        this.errorMessage = "Passwords do not match.";
        return;
      }
      if (this.password.length < 8) {
        this.errorMessage = "Password must be at least 8 characters.";
        return;
      }

      this.loading = true;
      try {
        await apiFetch("/reset-password", {
          method: "POST",
          body: JSON.stringify({
            token: this.token,
            password: this.password,
          }),
        });

        this.step = "success";
      } catch (error) {
        console.error("Reset password error:", error);
        // Distinguish an expired/invalid/used token from a generic failure,
        // if your API error response includes a code or specific message.
        if (error?.status === 400 || error?.status === 410) {
          this.step = "invalid";
        } else {
          this.errorMessage = "Something went wrong. Please try again.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dash-bg);
  padding: 24px;
  font-family: var(--dash-font);
}

.auth-card {
  background: white;
  border-radius: var(--dash-radius-xl);
  padding: 40px;
  max-width: 440px;
  width: 100%;
  box-shadow: var(--dash-shadow);
  border: 1px solid rgba(0, 0, 0, 0.02);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-muted);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
  transition: color 0.2s;
}

.back-link:hover {
  color: var(--dash-text);
}

.auth-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--dash-radius-lg);
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin-bottom: 20px;
}

.auth-icon.success {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.auth-icon.error {
  background: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

h1 {
  font-size: 22px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 10px;
}

.auth-subtitle {
  font-size: 14px;
  color: var(--dash-muted);
  line-height: 1.6;
  margin: 0 0 28px;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auth-form label {
  font-size: 13px;
  font-weight: 600;
  color: var(--dash-text);
  margin-top: 12px;
}

.auth-form label:first-child {
  margin-top: 0;
}

.auth-form input {
  padding: 12px 16px;
  border-radius: var(--dash-radius);
  border: 1px solid var(--dash-line);
  background: var(--dash-surface-2);
  font-size: 14px;
  font-family: inherit;
  color: var(--dash-ink);
  transition: all 0.2s;
}

.auth-form input:focus {
  outline: none;
  border-color: var(--dash-primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.auth-form input:disabled {
  opacity: 0.6;
}

.form-error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--dash-danger);
  margin: 4px 0 0;
}

.btn-primary {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 13px 20px;
  border-radius: var(--dash-pill);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: var(--dash-shadow);
  margin-top: 12px;
  text-align: center;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.btn-primary:hover:not(:disabled) {
  background: var(--dash-primary-hover);
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>